import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { X } from "@phosphor-icons/react";
import { CustomSection, customSectionSchema, defaultCustomSection } from "@reactive-resume/schema";
import {
  Badge,
  BadgeInput,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { DialogName, useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";
import { MonthYearDatePicker } from "../sections/shared/month-year-date-picker";
import { URLInput } from "../sections/shared/url-input";
import { MastersUnionListEditor } from "../sections/masters-union-summary";

const formSchema = customSectionSchema;

type FormValues = z.infer<typeof formSchema>;

type MastersUnionCustomFormat = "detailed" | "bullet";

type MastersUnionFieldsProps = {
  form: UseFormReturn<FormValues>;
  setPendingKeyword: Dispatch<SetStateAction<string>>;
};
const getCustomSectionFormat = (item?: Partial<CustomSection> | null): MastersUnionCustomFormat => {
  if (!item) return "detailed";

  const hasMeta =
    Boolean(item.name?.trim()) ||
    Boolean(item.description?.trim()) ||
    Boolean(item.date?.trim()) ||
    Boolean(item.location?.trim()) ||
    (item.keywords?.length ?? 0) > 0 ||
    Boolean(item.url?.href?.trim()) ||
    Boolean(item.url?.label?.trim()) ||
    Boolean(item.company?.label?.trim()) ||
    Boolean(item.company?.href?.trim()) ||
    Boolean(item.companyDescription?.trim()) ||
    (item.designations?.length ?? 0) > 0;

  return hasMeta ? "detailed" : "bullet";
};

function MastersUnionDetailedFields({ form, setPendingKeyword }: MastersUnionFieldsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField
        name="company"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Title</FormLabel>
            <FormControl>
              <URLInput
                {...field}
                value={
                  typeof field.value === "string"
                    ? { label: field.value, href: "" }
                    : (field.value ?? { label: "", href: "" })
                }
                placeholder="Company Name"
                labelPlaceholder="Company Website"
                disableAutoHttps={true}
                swapLabelAndHref={true}
                onChange={(value) => {
                  field.onChange(value);
                  form.setValue("name", value.label ?? "", { shouldValidate: true });
                  const prevUrl = form.getValues("url");
                  const urlBase =
                    typeof prevUrl === "object" && prevUrl !== null && "label" in prevUrl && "href" in prevUrl
                      ? prevUrl
                      : { label: "", href: "" };
                  form.setValue(
                    "url",
                    { ...urlBase, href: value.href },
                    { shouldValidate: true },
                  );
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name="description"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <span style={{ fontSize: "14px" }}>Description</span>
            </FormLabel>
            <FormControl>
              <Input {...field} maxLength={90} />
            </FormControl>
            <p className="text-xs text-muted-foreground">{`Character limit ${(field.value ?? "").length}/90`}</p>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name="date"
        control={form.control}
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Date or Date Range</FormLabel>
            <FormControl>
              <MonthYearDatePicker value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name="location"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Location</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* <FormField
        name="url"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Website</FormLabel>
            <FormControl>
              <URLInput {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      /> */}

      <FormField
        name="summary"
        control={form.control}
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Summary</FormLabel>
            <FormControl>
              <MastersUnionListEditor
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* <FormField
        name="keywords"
        control={form.control}
        render={({ field }) => (
          <div className="space-y-3 sm:col-span-2">
            <FormItem>
              <FormLabel>Keywords</FormLabel>
              <FormControl>
                <BadgeInput {...field} setPendingKeyword={setPendingKeyword} />
              </FormControl>
              <FormDescription>
                You can add multiple keywords by separating them with a comma or pressing enter.
              </FormDescription>
              <FormMessage />
            </FormItem>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
              <AnimatePresence>
                {field.value.map((item, index) => (
                  <motion.div
                    layout
                    key={item}
                    initial={{ opacity: 0, y: -50 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { delay: index * 0.1 },
                    }}
                    exit={{ opacity: 0, x: -50 }}
                  >
                    <Badge
                      className="cursor-pointer"
                      onClick={() => {
                        field.onChange(field.value.filter((v) => item !== v));
                      }}
                    >
                      <span className="mr-1">{item}</span>
                      <X size={12} weight="bold" />
                    </Badge>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      /> */}
    </div>
  );
}

function MastersUnionBulletFields({ form }: { form: UseFormReturn<FormValues> }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField
        name="summary"
        control={form.control}
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Summary</FormLabel>
            <FormControl>
              <MastersUnionListEditor
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

export const CustomSectionDialog = () => {
  const { payload, mode, isOpen, close } = useDialog<CustomSection>("custom");

  const form = useForm<FormValues>({
    defaultValues: defaultCustomSection,
    resolver: zodResolver(formSchema),
  });

  const [pendingKeyword, setPendingKeyword] = useState("");
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";
  const currentSection = useResumeStore((state) => {
    const sectionId = payload?.id;
    if (typeof sectionId !== "string" || !sectionId.startsWith("custom.")) return undefined;

    const customSectionId = sectionId.replace("custom.", "");
    return state.resume.data?.sections?.custom?.[customSectionId];
  });

  const [format, setFormat] = useState<MastersUnionCustomFormat>("detailed");
  const [createStep, setCreateStep] = useState<"pick-format" | "form">("pick-format");
  const [pickedFormat, setPickedFormat] = useState<MastersUnionCustomFormat>("detailed");

  useEffect(() => {
    if (!isOpen) {
      setCreateStep("pick-format");
      setPickedFormat("detailed");
      return;
    }

    if (isMastersUnion && mode === "create") {
      const defaultFormat = getCustomSectionFormat(currentSection?.items[0]);
      const hasExistingItems = (currentSection?.items.length ?? 0) > 0;

      setFormat(defaultFormat);
      setPickedFormat(defaultFormat);
      setCreateStep(hasExistingItems ? "form" : "pick-format");
    } else {
      setCreateStep("form");
    }
  }, [currentSection?.items, isOpen, isMastersUnion, mode, payload?.id]);

  useEffect(() => {
    if (!payload || !isMastersUnion || mode === "create") return;

    setFormat(getCustomSectionFormat(payload.item));
  }, [isMastersUnion, mode, payload]);

  if (!payload) return null;

  const showFormatPicker = isMastersUnion && mode === "create" && createStep === "pick-format" && !currentSection?.items.length;

  if (showFormatPicker) {
    return (
      <Dialog open onOpenChange={(next) => !next && close()}>
        <DialogContent className="z-50 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Choose how to add this item
            </DialogTitle>
            <DialogDescription>
              Pick a layout for this entry.
            </DialogDescription>
          </DialogHeader>

          <fieldset className="space-y-3">
            <legend className="sr-only">Item format</legend>

            <label
              className={cn(
                "flex cursor-pointer gap-3 rounded-md border p-4 transition-colors",
                pickedFormat === "detailed"
                  ? "border-primary bg-secondary/40"
                  : "border-border hover:bg-secondary/20",
              )}
            >
              <input
                type="radio"
                name="custom-section-format"
                className="mt-1"
                checked={pickedFormat === "detailed"}
                onChange={() => setPickedFormat("detailed")}
              />
              <div>
                <p className="font-medium">
                  Detailed section
                </p>
                <p className="text-sm text-muted-foreground">
                Name, dates, location, website, summary, and keywords.
                </p>
              </div>
            </label>

            <label
              className={cn(
                "flex cursor-pointer gap-3 rounded-md border p-4 transition-colors",
                pickedFormat === "bullet"
                  ? "border-primary bg-secondary/40"
                  : "border-border hover:bg-secondary/20",
              )}
            >
              <input
                type="radio"
                name="custom-section-format"
                className="mt-1"
                checked={pickedFormat === "bullet"}
                onChange={() => setPickedFormat("bullet")}
              />
              <div>
                <p className="font-medium">
                Bullet points
                </p>
                <p className="text-sm text-muted-foreground">
                Summary only — ideal for simple bullet lists.
                </p>
              </div>
            </label>
          </fieldset>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setFormat(pickedFormat);
                setCreateStep("form");
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <SectionDialog<FormValues>
      form={form}
      id={payload.id as DialogName}
      defaultValues={defaultCustomSection}
      pendingKeyword={pendingKeyword}
    >
      {isMastersUnion ? (
        <div className="space-y-4">
          {format === "detailed" ? (
            <MastersUnionDetailedFields form={form} setPendingKeyword={setPendingKeyword} />
          ) : (
            <MastersUnionBulletFields form={form} />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="description"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span style={{ fontSize: "14px" }}>Description</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} maxLength={90} />
                </FormControl>
                <p className="text-xs text-muted-foreground">{`Character limit ${(field.value ?? "").length}/90`}</p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="date"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Date or Date Range</FormLabel>
                <FormControl>
                  <MonthYearDatePicker value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="location"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Location</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="url"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <URLInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="summary"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Summary</FormLabel>
                <FormControl>
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        content={editor.getHTML()}
                        onChange={(value) => {
                          editor.commands.setContent(value);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="keywords"
            control={form.control}
            render={({ field }) => (
              <div className="space-y-3 sm:col-span-2">
                <FormItem>
                  <FormLabel>Keywords</FormLabel>
                  <FormControl>
                    <BadgeInput {...field} setPendingKeyword={setPendingKeyword} />
                  </FormControl>
                  <FormDescription>
                    You can add multiple keywords by separating them with a comma or pressing enter.
                  </FormDescription>
                  <FormMessage />
                </FormItem>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                  <AnimatePresence>
                    {field.value.map((item, index) => (
                      <motion.div
                        layout
                        key={item}
                        initial={{ opacity: 0, y: -50 }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          transition: { delay: index * 0.1 },
                        }}
                        exit={{ opacity: 0, x: -50 }}
                      >
                        <Badge
                          className="cursor-pointer"
                          onClick={() => {
                            field.onChange(field.value.filter((v) => item !== v));
                          }}
                        >
                          <span className="mr-1">{item}</span>
                          <X size={12} weight="bold" />
                        </Badge>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </SectionDialog>
  );
};
